/**
 * missingInfoProgress — client-side helpers that make the chat
 * "Resolve with Asthra" / edit-highlight flow legible to the writer,
 * computed ONLY from data the add-in already has (the highlighted text,
 * the SSE events the rewrite stream already delivers, and the proposed
 * replacement). No backend calls.
 *
 *  - Auto-instruction summary: the long canned instruction the resolve
 *    chip sends is collapsed to a short label + flag count.
 *  - Flag counting / per-flag outcome: ``[MISSING INFO: …]`` markers in the
 *    original selection vs. the proposed rewrite (resolved vs. kept).
 *  - Live step list: a reducer over the selection-edit SSE vocabulary
 *    (status / plan_ready / task_* / retrieval_step / tool_call_* /
 *    drafting_response), published through a tiny external store so the
 *    chat timeline can render it without new shell wiring.
 */

import { useSyncExternalStore } from "react";
import { describeToolCallFromQuery } from "../../config/toolDisplayNames";
import { isMissingInfoInstruction } from "./resolveInstruction";

// ---------------------------------------------------------------------------
// Missing-info markers
// ---------------------------------------------------------------------------

/** Fill-in placeholder markers — ``[MISSING INFO: body]``, ``[FILL: body]``,
 *  ``[PENDING: body]``, ``[NOT FOUND - …]`` (also tolerates ``-``/``–``
 *  separators and a bare ``[MISSING INFO]``). The registry's placeholder flags
 *  (server ``placeholder_fill.PLACEHOLDER_FLAG_IDS``); prefixes as in draftSanitizer. */
const MISSING_INFO_RE = /\[\s*(?:MISSING\s+INFO|FILL|PENDING|NOT\s+FOUND)\s*(?:[:\-–]\s*([^\]]*))?\]/gi;

/** The body of every missing-info marker in ``text``, in order. */
export function extractMissingInfoFlags(text: string | null | undefined): string[] {
  if (!text) return [];
  const out: string[] = [];
  for (const m of text.matchAll(MISSING_INFO_RE)) out.push((m[1] ?? "").trim());
  return out;
}

export function countMissingInfoFlags(text: string | null | undefined): number {
  return extractMissingInfoFlags(text).length;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export interface FlagOutcome {
  /** Flag bodies present in the original and gone from the rewrite. */
  resolved: string[];
  /** Flag bodies still present in the rewrite. */
  kept: string[];
}

const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

/** Per-flag outcome of a rewrite: each original flag is "kept" when the same
 *  flag (by body, whitespace/case-insensitive) is still in the replacement,
 *  otherwise "resolved". Multiset-aware so duplicate flags count separately. */
export function summarizeFlagOutcome(original: string, replacement: string): FlagOutcome {
  const before = extractMissingInfoFlags(original);
  const remaining = extractMissingInfoFlags(replacement).map(norm);
  const resolved: string[] = [];
  const kept: string[] = [];
  for (const flag of before) {
    const idx = remaining.indexOf(norm(flag));
    if (idx >= 0) {
      kept.push(flag);
      remaining.splice(idx, 1);
    } else {
      resolved.push(flag);
    }
  }
  // Rewrite reworded a flag instead of keeping it verbatim: treat the surplus
  // markers as kept (by count) so the totals never claim more were resolved
  // than actually disappeared.
  while (remaining.length > 0 && resolved.length > 0) {
    kept.push(resolved.pop() as string);
    remaining.pop();
  }
  return { resolved, kept };
}

/** Initial, honest status line for an edit-highlight run before any server
 *  step lands. Null when the selection has no flags (caller falls back). */
export function initialEditProgressLabel(selectedText: string): string | null {
  const n = countMissingInfoFlags(selectedText);
  if (n === 0) return null;
  return `Looking for answers to ${plural(n, "missing-info flag")} in your library…`;
}

// ---------------------------------------------------------------------------
// Auto-generated instruction → short label
// ---------------------------------------------------------------------------

const REGULATORY_PREFIX = "Resolve this specific regulatory flag by rewriting the highlighted text.";

export interface AutoInstructionSummary {
  /** Short label shown in place of the full instruction. */
  label: string;
  /** Flags counted in the selection, when there is one. */
  flagCount: number | null;
}

/** When ``text`` is one of the canned "Resolve with Asthra" instructions,
 *  return a short writer-facing label (with the flag count from the
 *  selection, when available); otherwise null (render the text as-is). */
export function summarizeAutoInstruction(
  text: string,
  selectionContext?: string | null,
): AutoInstructionSummary | null {
  const t = text.trim();
  const count = selectionContext ? countMissingInfoFlags(selectionContext) : null;
  if (isMissingInfoInstruction(t)) {
    const suffix = count ? ` · ${plural(count, "flag")} found` : "";
    return { label: `Resolve missing info in your selection${suffix}`, flagCount: count };
  }
  if (t.startsWith(REGULATORY_PREFIX)) {
    return { label: "Resolve regulatory flag in your selection", flagCount: count };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Live step list
// ---------------------------------------------------------------------------

export type EditStepStatus = "running" | "done" | "error";

export interface EditStep {
  id: string;
  name: string;
  summary?: string;
  /** Right-aligned result count ("3 results"). */
  meta?: string;
  status: EditStepStatus;
}

/** Steps shown before any server event: the flag count found client-side. */
export function initialEditSteps(selectedText: string): EditStep[] {
  const n = countMissingInfoFlags(selectedText);
  if (n === 0) return [];
  return [{ id: "flags", name: `Found ${plural(n, "missing-info flag")} in your selection`, status: "done" }];
}

function truncate(s: string, max = 70): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** True once a step beyond the client-side flag count exists — i.e. the
 *  server has reported real work (a lookup, a tool call, a plan). */
export function hasServerWork(steps: readonly EditStep[]): boolean {
  return steps.some((s) => s.id !== "flags");
}

/** Close every still-running step (a newer step has started). */
function settleRunning(steps: EditStep[]): EditStep[] {
  return steps.map((s) => (s.status === "running" ? { ...s, status: "done" } : s));
}

function upsert(steps: EditStep[], step: EditStep): EditStep[] {
  const i = steps.findIndex((s) => s.id === step.id);
  if (i < 0) return [...steps, step];
  const next = steps.slice();
  next[i] = { ...steps[i], ...step };
  return next;
}

/** Whether a ``drafting_response`` event is premature: the agent-loop path
 *  sends "Rewriting…" before the agent has looked anything up, so it is only
 *  meaningful once the server reported real work (or for a non-flag edit). */
export function isPrematureDrafting(event: Record<string, unknown>, steps: readonly EditStep[]): boolean {
  return String(event.type ?? "") === "drafting_response" && steps.length > 0 && !hasServerWork(steps);
}

/** Fold one selection-edit SSE event into the step list. Unknown events
 *  leave the list unchanged. */
export function reduceEditSteps(steps: EditStep[], event: Record<string, unknown>): EditStep[] {
  const type = String(event.type ?? "");
  switch (type) {
    case "status": {
      const label = str(event.label);
      if (!label) return steps;
      return upsert(settleRunning(steps), { id: `status:${label}`, name: label.replace(/…$/, ""), status: "running" });
    }
    case "plan_ready": {
      const total = Number(event.total_tasks ?? 0);
      const name = total > 0 ? `Planned ${plural(total, "library lookup")}` : "No lookups needed";
      return upsert(settleRunning(steps), { id: "plan", name, status: "done" });
    }
    case "task_start": {
      const query = str(event.need) || str(event.title);
      const id = `task:${str(event.id) || query}`;
      const name = query ? `Searching library for “${truncate(query)}”` : "Searching library";
      return upsert(steps, { id, name, status: "running" });
    }
    case "retrieval_step": {
      const msg = str(event.message) || str(event.outcome) || str(event.hint);
      if (!msg) return steps;
      const taskId = str(event.task_id);
      if (taskId && steps.some((s) => s.id === `task:${taskId}`)) {
        return upsert(steps, {
          id: `task:${taskId}`,
          name: steps.find((s) => s.id === `task:${taskId}`)?.name ?? "",
          summary: truncate(msg, 60),
          status: "running",
        });
      }
      // "Searching sources: Q" and the later "N result(s) for: Q" describe
      // one call — fold them into one row with the count on the right.
      const searching = /^Searching sources:\s*(.+)$/.exec(msg);
      if (searching) {
        const q = searching[1].trim();
        return upsert(steps, { id: `search:${q}`, name: `Searching sources for “${truncate(q)}”`, status: "running" });
      }
      const found = /^(\d+) result\(s\) for:\s*(.+)$/.exec(msg);
      if (found) {
        const n = Number(found[1]);
        const q = found[2].trim();
        return upsert(steps, {
          id: `search:${q}`,
          name: `Searching sources for “${truncate(q)}”`,
          meta: plural(n, "result"),
          status: n > 0 ? "done" : "error",
        });
      }
      return upsert(steps, { id: `step:${msg}`, name: truncate(msg), status: "done" });
    }
    case "task_done": {
      const id = `task:${str(event.id)}`;
      const existing = steps.find((s) => s.id === id);
      const level = str(event.level);
      const summary = str(event.summary) || (level ? `Found in library (${level})` : "Found in library");
      if (!existing) return steps;
      return upsert(steps, { ...existing, summary: truncate(summary, 60), status: "done" });
    }
    case "task_failed": {
      const id = `task:${str(event.id)}`;
      const existing = steps.find((s) => s.id === id);
      if (!existing) return steps;
      return upsert(steps, { ...existing, summary: "Not found", status: "error" });
    }
    case "tool_call_start": {
      const tool = str(event.tool_name);
      if (!tool) return steps;
      const query = str(event.tool_query);
      const id = `tool:${tool}:${query}`;
      if (tool === "submit_rewrite") {
        return upsert(settleRunning(steps), {
          id: "rewrite",
          name: "Rewriting the highlighted text",
          status: "running",
        });
      }
      return upsert(steps, { id, name: describeToolCallFromQuery(tool, query, false), status: "running" });
    }
    case "tool_call_result": {
      const tool = str(event.tool_name);
      if (!tool || tool === "submit_rewrite") return steps;
      const query = str(event.tool_query);
      const id = `tool:${tool}:${query}`;
      const ok = event.ok !== false;
      return upsert(steps, {
        id,
        name: describeToolCallFromQuery(tool, query, true),
        status: ok ? "done" : "error",
        ...(ok ? {} : { summary: "Failed" }),
      });
    }
    case "drafting_response": {
      if (isPrematureDrafting(event, steps)) return steps;
      return upsert(settleRunning(steps), { id: "rewrite", name: "Rewriting the highlighted text", status: "running" });
    }
    default:
      return steps;
  }
}

// ---------------------------------------------------------------------------
// External store — written by useHighlightEditFlow, read by TimelineView
// ---------------------------------------------------------------------------

let current: EditStep[] | null = null;
const listeners = new Set<() => void>();

export function setEditSteps(steps: EditStep[] | null): void {
  if (steps === current) return;
  current = steps;
  for (const l of listeners) l();
}

export function getEditSteps(): EditStep[] | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Live edit-highlight steps (null when no edit is streaming). */
export function useEditSteps(): EditStep[] | null {
  return useSyncExternalStore(subscribe, getEditSteps, getEditSteps);
}
